import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { createNewTopic } from "../../../apis/CourseApis";
import { useAuth } from "../../Authentication/AuthContext";
import { useToastHelpers } from "../../Toasts/ToastUtils";
import { useTheme } from "../../Theme/ThemeContext";
import SeoMeta from "../../SeoMeta";
import AddTheoryTopic from "./add/AddTheoryTopic";
import AddVideoTopic from "./add/AddVideoTopic";
import AddMCQTopic from "./add/AddMCQTopic";
import AddCodingTopic from "./add/AddCodingTopic";
import { DEFAULT_BOILERPLATE_CODES } from "../../../assets/boilerplateConstants";

export default function AddTopic() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const chapterId = searchParams.get("chapter");
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const { successToast, errorToast } = useToastHelpers();

  const [isJsonImportOpen, setIsJsonImportOpen] = useState(false);
  const [jsonText, setJsonText] = useState("{}");
  const [jsonParseError, setJsonParseError] = useState("");
  const [jsonParsed, setJsonParsed] = useState(null);

  const [topicType, setTopicType] = useState("theory");
  const [formData, setFormData] = useState({
    title: "",
    question: "",
    points: "",
    type: "theory",
    chapterId: chapterId,
    theoryContent: [""],
    videoUrl: "",
    mcq: { question: "", options: ["", "", "", ""], correctOption: 0, hasCode: false, code: "" },
    inputFormat: "",
    outputFormat: "",
    constraints: [""],
    examples: [{ input: "", expected: "" }],
    difficulty: "easy",
    boilerplateCodes: DEFAULT_BOILERPLATE_CODES,
    visibleTestCases: [{ input: "", expected: "" }],
    hiddenTestCases: [{ input: "", expected: "" }],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      type: topicType,
    }));
  }, [topicType]);

  useEffect(() => {
    if (!isJsonImportOpen) return;

    try {
      const parsed = JSON.parse(jsonText);
      if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) {
        setJsonParsed(null);
        setJsonParseError("JSON must be an object");
        return;
      }

      setJsonParsed(parsed);
      setJsonParseError("");
    } catch (e) {
      setJsonParsed(null);
      setJsonParseError("Invalid JSON");
    }
  }, [jsonText, isJsonImportOpen]);

  const applyJsonToForm = () => {
    if (!jsonParsed) return;

    const splitGoBlockFromText = (raw) => {
      const text = String(raw ?? "");
      const start = text.indexOf("go:");
      if (start === -1) return { text: text.trim(), code: "" };
      const end = text.indexOf(":go", start + 3);
      if (end === -1) return { text: text.trim(), code: "" };

      const code = text.slice(start + 3, end).replace(/^\n/, "").trimEnd();
      const before = text.slice(0, start).trimEnd();
      const after = text.slice(end + 3).trimStart();
      const cleaned = [before, after].filter(Boolean).join("\n").trim();
      return { text: cleaned, code };
    };

    const parsedType = String(jsonParsed.type || "").toLowerCase();
    const nextTopicType = ["theory", "video", "mcq", "coding"].includes(parsedType)
      ? parsedType
      : topicType;

    const normalizeStringArray = (value) => {
      if (Array.isArray(value)) return value.map((v) => String(v ?? "")).filter((v) => v.length > 0);
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed) return [];
        return trimmed.split("\n\n").map((s) => s.trim()).filter(Boolean);
      }
      return [];
    };

    const normalizeTestCases = (value) => {
      if (!Array.isArray(value)) return [];
      return value
        .filter(Boolean)
        .map((tc) => ({
          id: tc?.id,
          input: String(tc?.input ?? ""),
          expected: String(tc?.expected ?? ""),
        }));
    };

    const pointsValue = jsonParsed.points ?? jsonParsed.point;
    const nextPoints = pointsValue === undefined || pointsValue === null ? "" : String(pointsValue);

    setTopicType(nextTopicType);
    setFormData((prev) => {
      const next = {
        ...prev,
        title: typeof jsonParsed.title === "string" ? jsonParsed.title : prev.title,
        points: nextPoints !== "" ? nextPoints : prev.points,
        type: nextTopicType,
        chapterId: chapterId,
      };

      if (nextTopicType === "theory") {
        const content = normalizeStringArray(jsonParsed.theoryContent ?? jsonParsed.content);
        if (content.length > 0) next.theoryContent = content;
      }

      if (nextTopicType === "video") {
        if (typeof jsonParsed.videoUrl === "string") next.videoUrl = jsonParsed.videoUrl;
      }

      if (nextTopicType === "mcq") {
        const mcqSource = Array.isArray(jsonParsed.mcq) ? jsonParsed.mcq[0] : jsonParsed.mcq;
        if (mcqSource && typeof mcqSource === "object") {
          const options = Array.isArray(mcqSource.options) ? mcqSource.options.map((o) => String(o ?? "")) : prev.mcq.options;
          const paddedOptions = [...options];
          while (paddedOptions.length < 4) paddedOptions.push("");

          const rawQuestion = typeof mcqSource.question === "string" ? mcqSource.question : prev.mcq.question;
          const { text: baseQuestion, code } = splitGoBlockFromText(rawQuestion);

          next.mcq = {
            question: baseQuestion,
            options: paddedOptions.slice(0, 4),
            correctOption:
              Number.isInteger(mcqSource.answer)
                ? mcqSource.answer
                : Number.isInteger(mcqSource.correctOption)
                  ? mcqSource.correctOption
                  : prev.mcq.correctOption,
            hasCode: Boolean(code),
            code,
          };
        }
      }

      if (nextTopicType === "coding") {
        const codingSource = jsonParsed.codingProblem && typeof jsonParsed.codingProblem === "object"
          ? jsonParsed.codingProblem
          : jsonParsed;

        next.question = typeof codingSource.question === "string" ? codingSource.question : prev.question;
        next.inputFormat = typeof codingSource.inputFormat === "string" ? codingSource.inputFormat : prev.inputFormat;
        next.outputFormat = typeof codingSource.outputFormat === "string" ? codingSource.outputFormat : prev.outputFormat;
        next.constraints = normalizeStringArray(codingSource.constraints);
        if (next.constraints.length === 0) next.constraints = prev.constraints;

        if (Array.isArray(codingSource.examples)) {
          next.examples = codingSource.examples.map((ex) => ({
            input: String(ex?.input ?? ""),
            expected: String(ex?.expected ?? ""),
          }));
        }

        const visible = normalizeTestCases(codingSource.testCases ?? codingSource.visibleTestCases);
        const hidden = normalizeTestCases(codingSource.hiddenTestCases);
        if (visible.length > 0) next.visibleTestCases = visible;
        if (hidden.length > 0) next.hiddenTestCases = hidden;

        if (typeof codingSource.difficulty === "string") next.difficulty = codingSource.difficulty;

        if (codingSource.boilerplate && typeof codingSource.boilerplate === "object") {
          next.boilerplateCodes = codingSource.boilerplate;
        }
        if (codingSource.boilerplateCodes && typeof codingSource.boilerplateCodes === "object") {
          next.boilerplateCodes = codingSource.boilerplateCodes;
        }
      }

      return next;
    });

    setIsJsonImportOpen(false);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Topic title is required";
    }

    if (topicType === "video" && !formData.videoUrl.trim()) {
      newErrors.videoUrl = "Video URL is required";
    }

    if (topicType === "mcq" && !formData.mcq.question.trim()) {
      newErrors.mcq = "Question is required";
    }

    if (topicType === "mcq" || topicType === "coding") {
      const parsedPoints = Number.parseInt(formData.points, 10);
      if (formData.points === "" || Number.isNaN(parsedPoints) || parsedPoints < 0) {
        newErrors.point = "Points must be a non-negative integer";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const now = Date.now();
      const parsedPoints = Number.parseInt(formData.points, 10);

      const buildMcqQuestion = (questionText, code, hasCode) => {
        const q = String(questionText ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").trimEnd();
        const c = String(code ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").trimEnd();
        if (!hasCode || !c.trim()) return q.trim();
        // Ensure exactly one go block appended.
        return `${q.trim()}\n\ngo:${c}:go`;
      };

      const normalizeIoText = (value) =>
        String(value ?? "")
          .replace(/\r\n/g, "\n")
          .replace(/\\n/g, "\n");

      const topicData = {
        title: formData.title,
        type: topicType,
        chapterId: chapterId,
        createdBy: user?.userId,
        createdAt: now,
        updatedAt: now,
      };

      if (topicType === "mcq" || topicType === "coding") {
        topicData.points = Number.isNaN(parsedPoints) ? 0 : parsedPoints;
      }

      if (topicType === "theory") {
        topicData.theoryContent = formData.theoryContent.filter((p) => p.trim());
      } else if (topicType === "video") {
        topicData.videoUrl = formData.videoUrl;
      } else if (topicType === "mcq") {
        topicData.mcq = {
          question: buildMcqQuestion(formData.mcq.question, formData.mcq.code, formData.mcq.hasCode),
          options: formData.mcq.options,
          answer: formData.mcq.correctOption,
        };
      } else if (topicType === "coding") {
        // Add IDs to test cases for backend execution tracking
        const testCasesWithIds = formData.visibleTestCases.map((tc, idx) => ({
          id: tc.id || `tc-${idx + 1}`,
          input: normalizeIoText(tc.input),
          expected: normalizeIoText(tc.expected),
        }));
        const hiddenTestCasesWithIds = formData.hiddenTestCases.map((tc, idx) => ({
          id: tc.id || `ht-${idx + 1}`,
          input: normalizeIoText(tc.input),
          expected: normalizeIoText(tc.expected),
        }));

        const normalizedExamples = (formData.examples || []).map((ex) => ({
          ...ex,
          input: normalizeIoText(ex?.input),
          expected: normalizeIoText(ex?.expected),
        }));

        topicData.codingProblem = {
          title: formData.title,
          question: formData.question,
          inputFormat: formData.inputFormat,
          outputFormat: formData.outputFormat,
          constraints: formData.constraints.filter((c) => c.trim()),
          examples: normalizedExamples,
          testCases: testCasesWithIds,
          hiddenTestCases: hiddenTestCasesWithIds,
          difficulty: formData.difficulty,
          boilerplate: formData.boilerplateCodes,
        };
      }

      await createNewTopic(topicData, user?.token, chapterId);
      successToast("Topic created successfully!");
      navigate(`/chapter/${chapterId}`);
    } catch (err) {
      console.error("Error creating topic:", err);
      errorToast("Failed to create topic. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SeoMeta
        title="Add Topic - Code Arena Admin"
        description="Create a new topic"
        url="https://www.code-arena.in/topics/new"
        image="/logo192.png"
      />

      <div className="ca-container max-w-4xl py-8">
        <button
          onClick={() => navigate(`/chapter/${chapterId}`)}
          className="flex items-center gap-2 font-medium mb-6 transition-colors"
          style={{ color: 'var(--color-primary)' }}
        >
          <FaArrowLeft /> Back to Topics
        </button>

        <div className="ca-card overflow-hidden">
          <div className={`bg-gradient-to-r ${currentTheme.colors.gradient} px-4 sm:px-8 py-5 sm:py-6`}>
            <h1 className="text-3xl font-bold text-white">Create New Topic</h1>
            <p className="text-white/80 mt-2">Add a new learning topic</p>
          </div>

          <form onSubmit={handleSubmit} className="p-4 sm:p-8 space-y-8">
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between gap-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Topic Title *
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setIsJsonImportOpen(true);
                      setJsonParseError("");
                    }}
                    className="ca-btn-secondary px-4 py-2 text-sm"
                  >
                    Import JSON
                  </button>
                </div>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Understanding React Hooks"
                  className={`ca-input py-3 ${
                    errors.title
                      ? "border-red-500 focus:border-red-500 focus:ring-red-600/20"
                      : ""
                  }`}
                />
                {errors.title && (
                  <p className="text-red-600 text-sm mt-1">{errors.title}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-4">
                  Topic Type *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {["theory", "video", "mcq", "coding"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setTopicType(type)}
                      className={`p-3 sm:p-4 rounded-xl border transition-all font-medium capitalize ${
                        topicType === type
                          ? "bg-white text-blue-700 shadow-sm ring-1 ring-black/5 border-gray-200"
                          : "bg-white/70 text-gray-800 border-gray-200 hover:bg-white"
                      }`}
                    >
                      {type === "theory" && "📝 Theory"}
                      {type === "video" && "🎥 Video"}
                      {type === "mcq" && "📋 MCQ"}
                      {type === "coding" && "💻 Coding"}
                    </button>
                  ))}
                </div>
              </div>

              {(topicType === "mcq" || topicType === "coding") && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Points *
                  </label>
                  <input
                    type="number"
                    name="points"
                    value={formData.points}
                    onChange={handleInputChange}
                    placeholder={topicType === "coding" ? "e.g., 80" : "e.g., 20"}
                    min="0"
                    step="1"
                    className={`ca-input py-3 ${
                      errors.point
                        ? "border-red-500 focus:border-red-500 focus:ring-red-600/20"
                        : ""
                    }`}
                  />
                  {errors.point && (
                    <p className="text-red-600 text-sm mt-1">{errors.point}</p>
                  )}
                </div>
              )}
            </div>

            {/* Theory */}
            {topicType === "theory" && <AddTheoryTopic formData={formData} setFormData={setFormData} />}

            {/* Video */}
            {topicType === "video" && <AddVideoTopic formData={formData} setFormData={setFormData} errors={errors} />}

            {/* MCQ (Single) */}
            {topicType === "mcq" && <AddMCQTopic formData={formData} setFormData={setFormData} errors={errors} />}

            {/* Coding */}
            {topicType === "coding" && <AddCodingTopic formData={formData} setFormData={setFormData} />}

            <div className="pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="ca-btn-primary w-full py-3"
              >
                {isSubmitting ? "Creating..." : "Create Topic"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {isJsonImportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="ca-card w-full max-w-3xl p-5 sm:p-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Import Topic JSON</h2>
                <p className="text-sm text-gray-600">Paste JSON and click Apply to fill the form.</p>
              </div>
              <button
                type="button"
                onClick={() => setIsJsonImportOpen(false)}
                className="ca-btn-secondary px-4 py-2 text-sm"
              >
                Close
              </button>
            </div>

            <textarea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              placeholder='{"type":"coding","title":"Two Sum","points":80,"codingProblem":{...}}'
              className="ca-input py-3 font-mono text-xs min-h-[260px]"
              spellCheck={false}
            />

            <div className="mt-3 flex items-center justify-between gap-3">
              <div className="text-sm">
                {jsonParseError ? (
                  <span className="text-red-600 font-medium">{jsonParseError}</span>
                ) : (
                  <span className="text-green-700 font-medium">JSON looks valid</span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setIsJsonImportOpen(false)}
                  className="ca-btn-secondary px-5 py-2"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={applyJsonToForm}
                  disabled={!jsonParsed || !!jsonParseError}
                  className="ca-btn-primary px-5 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
